/**
 * Home — `/` (trang chủ).
 *
 * Dashboard sinh viên theo kỳ học: điểm từng môn trong 1 kỳ (chọn được), kỳ
 * ĐÃ QUA tô màu cột điểm theo đậu (xanh)/rớt (đỏ) — kỳ đang diễn ra tô xanh
 * dương trung tính (chưa đủ dữ liệu kết luận đậu/rớt). Mirror dashboard
 * giảng viên ở trang admin (`admin/routes/Dashboard.tsx`) nhưng scope theo
 * CHÍNH BẠN thay vì trung bình cả lớp.
 */
import { useState } from "react";
import { Alert, Card, Col, Empty, Row, Select, Space, Spin, Statistic, Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { type MyTermDashboardCourse, useMyTermDashboard } from "@/api/assessment";
import { ScoreTag, pctTo10 } from "@/components/ScoreTag";
import { useAuthStore } from "@/stores/auth";

const { Title, Text } = Typography;

const PASS_COLOR = "#52c41a";
const FAIL_COLOR = "#ff4d4f";
const CURRENT_TERM_COLOR = "#1677ff";

export function Home() {
  const user = useAuthStore((s) => s.user);
  const [selectedTermId, setSelectedTermId] = useState<number | undefined>(undefined);
  const query = useMyTermDashboard(selectedTermId);

  const header = (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Title level={2} style={{ margin: 0 }}>
        Welcome, {user?.first_name || user?.username || "there"}!
      </Title>
      {user && (
        <Space>
          <Tag icon={<UserOutlined />} color="blue">
            {user.role}
          </Tag>
          <Tag color="processing">{user.email}</Tag>
          {user.tenant && <Tag color="green">Tenant: {user.tenant.name}</Tag>}
        </Space>
      )}
    </Space>
  );

  if (query.isPending) {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Card>{header}</Card>
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Card>{header}</Card>
        <Alert type="error" showIcon message="Unable to load grades" style={{ marginTop: 16 }} />
      </div>
    );
  }

  const { term, terms, courses } = query.data;
  const activeTermId = selectedTermId ?? term?.id;

  const termPicker = terms.length > 0 && (
    <Select
      style={{ width: 240 }}
      placeholder="Select term"
      value={activeTermId}
      options={terms.map((t) => ({
        value: t.id,
        label: t.is_current ? `${t.name} (Current)` : t.name,
      }))}
      onChange={(v) => setSelectedTermId(v)}
    />
  );

  if (!term) {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Card>{header}</Card>
        <Empty
          style={{ marginTop: 24 }}
          description="You haven't enrolled in any course yet."
        />
      </div>
    );
  }

  const gradedCourses = courses.filter((c) => c.total_pct != null);
  const passCount = courses.filter((c) => c.is_pass === true).length;
  const failCount = courses.filter((c) => c.is_pass === false).length;

  const chartData = gradedCourses.map((c) => ({
    code: c.code,
    grade: Number(pctTo10(c.total_pct ?? 0).toFixed(2)),
    color: c.is_pass == null ? CURRENT_TERM_COLOR : c.is_pass ? PASS_COLOR : FAIL_COLOR,
  }));

  const columns: TableColumnsType<MyTermDashboardCourse> = [
    { title: "Code", dataIndex: "code", key: "code", width: 140 },
    { title: "Course", dataIndex: "name", key: "name" },
    {
      title: "Quizzes/Attendance",
      dataIndex: "quiz_count",
      key: "quiz_count",
      width: 150,
      align: "right",
    },
    {
      title: "Grade",
      dataIndex: "total_pct",
      key: "total_pct",
      width: 100,
      render: (v: number | null) => <ScoreTag value={v == null ? null : pctTo10(v)} />,
    },
    {
      title: "Result",
      dataIndex: "is_pass",
      key: "is_pass",
      width: 110,
      render: (v: boolean | null) =>
        v == null ? (
          <Text type="secondary">—</Text>
        ) : v ? (
          <Tag color="green">Pass</Tag>
        ) : (
          <Tag color="red">Fail</Tag>
        ),
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card style={{ marginBottom: 16 }}>{header}</Card>

      <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
        <Title level={3} style={{ margin: 0 }}>
          My grades
        </Title>
        {termPicker}
      </Row>
      <Text type="secondary">
        Term: {term.name}{" "}
        {term.is_current && <Tag color="blue">Current</Tag>}
        {term.is_past && <Tag>Ended</Tag>}
      </Text>

      {term.is_past && (
        <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
          <Col xs={12}>
            <Card>
              <Statistic title="Courses passed" value={passCount} valueStyle={{ color: PASS_COLOR }} />
            </Card>
          </Col>
          <Col xs={12}>
            <Card>
              <Statistic title="Courses failed" value={failCount} valueStyle={{ color: FAIL_COLOR }} />
            </Card>
          </Col>
        </Row>
      )}

      <Card title="Grades by course" style={{ marginTop: 16, marginBottom: 16 }}>
        {chartData.length === 0 ? (
          <Empty description="No grades yet this term" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="code" angle={-20} textAnchor="end" height={60} />
              <YAxis domain={[0, 10]} />
              <Tooltip />
              <Bar dataKey="grade" name="Grade" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card>
        <Table<MyTermDashboardCourse>
          rowKey="id"
          columns={columns}
          dataSource={courses}
          pagination={false}
          locale={{ emptyText: <Empty description="No courses enrolled this term" /> }}
        />
      </Card>
    </div>
  );
}

export default Home;
